/**
 * YVA sections: small progressive enhancements.
 * Everything works without this file (links, forms, radios, details, scrolling); the script
 * adds gallery switching, the chosen option in the button and the fixed bar, the quantity
 * stepper, label filters, carousel buttons, copy-to-clipboard, the pop-up and the fixed purchase
 * bar. Loaded as a module, so it runs once even when several sections include it.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Splits a goals attribute ("ossos, musculo") into keys. */
function parseGoals(value) {
  return (value || '')
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean);
}

/** Formats an amount in cents in the store's currency (pt-BR by default). */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'BRL';
  const locale = document.documentElement.lang || 'pt-BR';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
  }
}

/** Product gallery: thumbnails swap the main image. */
class YvaGallery extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.addEventListener('click', (event) => this.onClick(event), { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumb = target.closest('[data-yva-thumb]');
    const main = this.querySelector('[data-yva-main]');
    if (!(thumb instanceof HTMLElement) || !(main instanceof HTMLImageElement)) return;

    for (const button of this.querySelectorAll('[data-yva-thumb]')) {
      button.setAttribute('aria-pressed', String(button === thumb));
    }

    main.classList.add('is-loading');
    main.addEventListener('load', () => main.classList.remove('is-loading'), { once: true });
    if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
    if (thumb.dataset.src) main.src = thumb.dataset.src;
    if (thumb.dataset.width) main.width = Number(thumb.dataset.width);
    if (thumb.dataset.height) main.height = Number(thumb.dataset.height);
    main.alt = thumb.dataset.alt || '';
    if (main.complete) main.classList.remove('is-loading');
  }
}

/**
 * Purchase options: keeps the button, the fixed bar and the quantity in step with the chosen
 * option. The quantity only goes with the one-time purchase; subscriptions ship one pouch.
 */
class YvaBuybox extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.addEventListener('change', (event) => this.onChange(event), { signal });
    this.addEventListener('input', (event) => this.onInput(event), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    document.addEventListener('yva:choose-plan', (event) => this.choose(event.detail), { signal });
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get quantity() {
    const input = this.querySelector('[data-yva-qty-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {Event} event */
  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'selling_plan') this.update();
    else if (target === this.quantity) this.setQuantity(Number.parseInt(target.value, 10));
  }

  /** @param {Event} event */
  onInput(event) {
    if (event.target === this.quantity) this.renderQuantity();
  }

  /** @param {Event} event */
  onClick(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-yva-qty-step]') : null;
    const input = this.quantity;
    if (!(button instanceof HTMLButtonElement) || !input || input.disabled) return;
    const current = Number.parseInt(input.value, 10) || 1;
    this.setQuantity(current + Number(button.dataset.yvaQtyStep));
  }

  /** @param {number} value */
  setQuantity(value) {
    const input = this.quantity;
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || min;
    const next = Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : min;
    input.value = String(next);
    this.renderQuantity();
  }

  /** Step buttons follow the limits; the total shows from two units on. */
  renderQuantity() {
    const input = this.quantity;
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || min;
    const count = Number.parseInt(input.value, 10);

    for (const button of this.querySelectorAll('[data-yva-qty-step]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const down = Number(button.dataset.yvaQtyStep) < 0;
      button.disabled = input.disabled || (down ? count <= min : count >= max);
    }

    const total = this.querySelector('[data-yva-qty-total]');
    const once = this.querySelector('input[name="selling_plan"][data-unit]');
    if (!(total instanceof HTMLElement) || !(once instanceof HTMLInputElement)) return;
    const unit = Number(once.dataset.unit);
    if (input.disabled || !Number.isFinite(count) || count < 2 || count > max || !Number.isFinite(unit)) {
      total.textContent = '';
      return;
    }
    const template = total.dataset.template || '[total]';
    total.textContent = template.replace('[total]', formatMoney(unit * count));
  }

  /** @param {string} kind - "subscription" moves a one-time choice to the default subscription */
  choose(kind) {
    if (kind !== 'subscription') return;
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.plan !== 'once') return;
    const subscriptions = 'input[name="selling_plan"]:not([data-plan="once"])';
    const preferred =
      this.querySelector(`${subscriptions}[data-default]`) || this.querySelector(subscriptions);
    if (preferred instanceof HTMLInputElement) {
      preferred.checked = true;
      this.update();
    }
  }

  update() {
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    if (!(checked instanceof HTMLInputElement)) return;
    const { price, label, cta, barCta, plan } = checked.dataset;

    const input = this.quantity;
    if (input) input.disabled = plan !== 'once';
    this.renderQuantity();

    const button = this.querySelector('[data-yva-cta]');
    if (button && cta) button.textContent = cta;

    // Before the launch the bar keeps the launch status instead of a price.
    if (this.classList.contains('yva-buy--soon')) return;
    const section = this.closest('.yva');
    const barPrice = section?.querySelector('[data-yva-bar-price]');
    if (barPrice && price) barPrice.textContent = label ? `${label} · ${price}` : price;
    const barButton = section?.querySelector('[data-yva-bar-cta]');
    if (barButton && barCta) barButton.textContent = barCta;
  }
}

/** Label: filter chips highlight the ingredients linked to a goal. */
class YvaLabel extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.hasAttribute('data-yva-goal-input')) {
          this.setGoal(target.value);
        }
      },
      { signal }
    );
    document.addEventListener('yva:goal', (event) => this.setGoal(event.detail), { signal });

    const checked = this.querySelector('[data-yva-goal-input]:checked');
    this.setGoal(checked instanceof HTMLInputElement ? checked.value : 'all');
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @param {string} goal */
  setGoal(goal) {
    const inputs = [...this.querySelectorAll('[data-yva-goal-input]')];
    const input = inputs.find((element) => element instanceof HTMLInputElement && element.value === goal);
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = true;

    const table = this.querySelector('[data-yva-table]');
    if (table instanceof HTMLElement) table.dataset.filter = goal;

    let index = 0;
    for (const row of this.querySelectorAll('.yva-nutri__row')) {
      if (!(row instanceof HTMLElement)) continue;
      const match = goal !== 'all' && parseGoals(row.dataset.goals).includes(goal);
      row.classList.toggle('is-match', match);
      row.style.setProperty('--n', match ? String(index++) : '0');
    }

    const claim = this.querySelector('[data-yva-claim]');
    if (claim && input.dataset.claim) claim.textContent = input.dataset.claim;
  }
}

/** Carousel: the buttons scroll the track by one card and turn off at the ends; swipe and scroll also work. */
class YvaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-yva-track]');
    if (!(this.track instanceof HTMLElement)) return;
    this.prev = this.querySelector('[data-yva-prev]');
    this.next = this.querySelector('[data-yva-next]');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.prev?.addEventListener('click', () => this.go(-1), { signal });
    this.next?.addEventListener('click', () => this.go(1), { signal });
    this.track.addEventListener('scroll', () => this.sync(), { passive: true, signal });
    window.addEventListener('resize', () => this.sync(), { signal });
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLElement && this.track?.contains(target)) {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      },
      { signal }
    );
    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @param {number} direction - -1 for previous, 1 for next */
  go(direction) {
    if (!this.track) return;
    const card = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: direction * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  sync() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev instanceof HTMLButtonElement) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = this.track.scrollLeft >= max;
  }
}

/** Mobile purchase bar: visible after the opening, hidden near the purchase block and footer. */
class YvaBuybar extends HTMLElement {
  connectedCallback() {
    const targetId = this.dataset.target;
    const purchase = targetId ? document.getElementById(targetId) : null;
    const opening = document.querySelector('.yva-hero');
    const footer = document.querySelector('.yva-footer') || document.querySelector('footer');
    this.button = this.querySelector('a, button');

    this.state = { pastOpening: !opening, nearPurchase: false, nearFooter: false };
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === opening) {
          this.state.pastOpening = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else if (entry.target === purchase) {
          this.state.nearPurchase = entry.isIntersecting;
        } else if (entry.target === footer) {
          this.state.nearFooter = entry.isIntersecting;
        }
      }
      this.render();
    });
    for (const element of [opening, purchase, footer]) {
      if (element) this.observer.observe(element);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render() {
    const visible = this.state.pastOpening && !this.state.nearPurchase && !this.state.nearFooter;
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    if (this.button) this.button.tabIndex = visible ? 0 : -1;
  }
}

/**
 * Pop-up (Starter Kit offer or launch list): opens once after a delay. Remembers dismissals for a
 * number of days, and signups and buy clicks for good. Re-opens on load right after its own form
 * was posted, to show the result.
 */
class YvaPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target === this.dialog) {
          this.close('dismissed');
        } else if (target instanceof Element && target.closest('[data-yva-popup-close]')) {
          this.close('dismissed');
        } else if (target instanceof Element && target.closest('[data-yva-popup-go]')) {
          // The offer button leads to the purchase: remember it and let the link work.
          this.close('done');
        }
      },
      { signal }
    );
    this.dialog.addEventListener('cancel', () => this.remember('dismissed'), { signal });
    this.dialog.addEventListener('submit', () => this.remember('done'), { signal });

    const sectionId = this.dataset.section;
    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (event.detail?.sectionId === sectionId) this.open();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (event.detail?.sectionId === sectionId) this.dialog.close();
      },
      { signal }
    );

    if (this.dataset.form && window.location.hash === `#${this.dataset.form}`) {
      if (this.dialog.querySelector('.yva-notify__ok')) this.remember('done');
      // Drop the hash so a later reload does not open the pop-up again.
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
      this.open();
      return;
    }
    if (window.Shopify?.designMode) return;
    if (document.querySelector('.yva-notify__ok')) {
      this.remember('done');
      return;
    }
    if (!this.shouldShow()) return;
    if (document.readyState === 'complete') {
      this.preloadImage();
    } else {
      window.addEventListener('load', () => this.preloadImage(), { once: true, signal });
    }
    this.timer = window.setTimeout(() => this.open(), Number(this.dataset.delay || 8) * 1000);
  }

  /** Loads the photo before the pop-up opens; fades it in if it still arrives after opening. */
  preloadImage() {
    const img = this.querySelector('.yva-popup__media img');
    if (!(img instanceof HTMLImageElement) || this.imageRequested) return;
    this.imageRequested = true;
    img.loading = 'eager';
    if (img.complete) return;
    img.addEventListener(
      'load',
      () => {
        // Without a repaint, a photo that finishes loading inside an open dialog can stay blank.
        if (this.dialog.open) img.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250 });
      },
      { once: true }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    window.clearTimeout(this.timer);
  }

  read() {
    try {
      return JSON.parse(window.localStorage.getItem('yva-popup') || 'null');
    } catch {
      return null;
    }
  }

  /** @param {'dismissed' | 'done'} state */
  remember(state) {
    if (this.read()?.state === 'done') return;
    try {
      window.localStorage.setItem('yva-popup', JSON.stringify({ state, at: Date.now() }));
    } catch {
      // Storage can be unavailable (private mode); the pop-up then simply shows again later.
    }
  }

  shouldShow() {
    const saved = this.read();
    if (!saved) return true;
    if (saved.state === 'done') return false;
    const days = Number(this.dataset.days || 14);
    return Date.now() - Number(saved.at || 0) > days * 86400000;
  }

  open() {
    if (this.dialog.open || document.querySelector('dialog[open]')) return;
    this.preloadImage();
    this.dialog.showModal();
  }

  /** @param {'dismissed' | 'done'} state */
  close(state) {
    this.remember(state);
    this.dialog.close();
  }
}

const elements = {
  'yva-gallery': YvaGallery,
  'yva-buybox': YvaBuybox,
  'yva-label': YvaLabel,
  'yva-buybar': YvaBuybar,
  'yva-popup': YvaPopup,
  'yva-carousel': YvaCarousel,
};
for (const [name, constructor] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

/** Global clicks: goal links open a label filter; plan links preselect the subscription; copy buttons. */
if (!window.__yvaClicks) {
  window.__yvaClicks = true;

  // Theme editor: selecting a formula highlight block opens its tab.
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('.yva-hl__tab');
    const radio = tab ? document.getElementById(tab.getAttribute('for') || '') : null;
    if (radio instanceof HTMLInputElement) radio.checked = true;
  });

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const goalLink = target.closest('[data-yva-goal]');
    if (goalLink instanceof HTMLAnchorElement) {
      const hash = goalLink.hash;
      const section = hash ? document.querySelector(hash) : null;
      if (section) {
        event.preventDefault();
        section.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', hash);
        const goal = goalLink.dataset.yvaGoal;
        window.setTimeout(
          () => document.dispatchEvent(new CustomEvent('yva:goal', { detail: goal })),
          reduceMotion.matches ? 0 : 500
        );
      }
      return;
    }

    const planLink = target.closest('[data-yva-plan]');
    if (planLink instanceof HTMLElement) {
      document.dispatchEvent(new CustomEvent('yva:choose-plan', { detail: planLink.dataset.yvaPlan }));
    }

    const copyButton = target.closest('[data-yva-copy]');
    if (copyButton instanceof HTMLButtonElement) {
      const source = document.getElementById(copyButton.dataset.yvaCopy || '');
      if (!source) return;
      const text = source.textContent?.trim() || '';
      const original = copyButton.textContent;
      try {
        await navigator.clipboard.writeText(text);
        copyButton.textContent = copyButton.dataset.copied || original;
        window.setTimeout(() => {
          copyButton.textContent = original;
        }, 2000);
      } catch {
        const range = document.createRange();
        range.selectNodeContents(source);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  });
}
